import { h } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import { FormattedChatText } from './FormattedChatText';

interface TypewriterTextProps {
  content: string;
  animate?: boolean;
  onUpdate?: () => void; // Called when displayed text grows
  onDone?: () => void; // Called when typing finishes (best-effort)
}

export function TypewriterText({ content, animate = true, onUpdate, onDone }: TypewriterTextProps) {
  // If we shouldn't animate, just render plain text
  if (!animate) {
    return <FormattedChatText content={content} />;
  }

  const [displayedLength, setDisplayedLength] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const targetRef = useRef(content);
  targetRef.current = content;

  useEffect(() => {
    let timeoutId: number;
    
    const typeNext = () => {
      const target = targetRef.current;
      
      setDisplayedLength(current => {
        // If we caught up or overshot, stop
        if (current >= target.length) {
          setIsTyping(false);
          try { onDone?.(); } catch {}
          return target.length;
        }

        setIsTyping(true);
        
        // Adaptive chunk size for smoother streaming
        const remaining = target.length - current;
        const chunk = remaining > 100 ? 4 : (remaining > 30 ? 2 : 1);
        const next = Math.min(current + chunk, target.length);
        
        // Trigger scroll callback
        onUpdate?.();
        
        return next;
      });

      // Randomized delay (15-40ms) for natural feel
      timeoutId = window.setTimeout(typeNext, 15 + Math.random() * 25);
    };

    timeoutId = window.setTimeout(typeNext, 20);

    return () => window.clearTimeout(timeoutId);
  }, [content]); // Re-run when target content updates

  const displayed = content.slice(0, displayedLength);

  return (
    <span>
      <FormattedChatText content={displayed} />
      {isTyping && <span class="streaming-cursor" />}
    </span>
  );
}
